import { SVG } from 'components/Asset/SVG';
import { IdSpan } from 'components/AgGrid/CellRenderer/ListView.styled';
import { theme } from 'mui.theme';
import { type Device as DeviceType } from 'types/device.types';
import { StyledIcon } from 'components/AgGrid/CellRenderer/CellRenderer.styled';
import { DEVICE_ORIENTATION } from 'utils/enums';
import {
  calculateTimeStampFromCurrentTime,
  convertUtcToDisplayTime,
} from 'utils/helpers/dateTime';
import { checkDeviceStatus } from 'store/utils';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { StatusDot } from 'components/StatusIndicator/IgnitionStatusIndicator';
import { isAssetIgnition } from 'views/FleetOverview/helpers/asset.helpers';
import { type AssetListRendererProp } from '../../AssetView/components/AssetListCellRenderer';
import { Box, Typography } from '@mui/material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';

const { svgIcons } = theme.palette;

interface Props {
  value: string;
  data: DeviceType & Record<string, number>;
  handleIdClick: (device_id: string) => void;
  handleDeleteDevice: (device_id: string) => void;
  handleOperatorClick: (device_id: string) => void;
  handleTagClick: (device_id: string) => void;
  handleDeviceClick: (device_id: string) => void;
}

export const DeleteAsset = ({
  data: { deviceId },
  handleDeleteDevice,
}: Props) => {
  return (
    <span
      onClick={() => {
        handleDeleteDevice(deviceId);
      }}
    >
      <SVG
        name="deleteIcon"
        style={{
          ...svgIcons?.primary,
        }}
      />
    </span>
  );
};

export const DeviceName = ({ handleIdClick, data: { deviceId } }: Props) => {
  return (
    <IdSpan
      onClick={() => {
        handleIdClick(deviceId);
      }}
    >
      {deviceId}
    </IdSpan>
  );
};

export const AssetId = ({
  handleIdClick,
  data: { deviceId, assetId },
}: Props) => {
  return (
    <IdSpan
      onClick={() => {
        handleIdClick(deviceId);
      }}
    >
      {assetId}
    </IdSpan>
  );
};

export const DeviceStatus = ({ data }: Props) => {
  const { t } = useTranslation();
  // 1. If NO asset id, device is unassociated to an asset -> show unlinked status
  if (!data.assetId) {
    return (
      <>
        <StyledIcon>
          <SVG name={'unlinkedDeviceStatusIcon'} />
        </StyledIcon>
        Unlinked
      </>
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMissing, isActive, isXYFound] = checkDeviceStatus(data);

  if (!isXYFound) {
    return (
      <>
        <StyledIcon>
          <SVG
            name={'offlineIcon'}
            style={{
              ...svgIcons.error,
            }}
          />
        </StyledIcon>
        <IdSpan>{t('missing_x_y')}</IdSpan>
      </>
    );
  }
  return (
    <span>
      <StyledIcon>
        <SVG
          name={
            data.connection_status === 'connected'
              ? 'activeIcon'
              : 'offlineIcon'
          }
          style={{
            fill: 'red',
          }}
        />
      </StyledIcon>
      {data.connection_status
        ? capitalizeFirstLetter(data.connection_status)
        : 'Unknown'}
    </span>
  );
};

export const xOrientation = ({ data: { xOrientation } }: Props) => {
  return (
    <>
      {DEVICE_ORIENTATION.find((device) => device.id === xOrientation)?.display}
    </>
  );
};

export const yOrientation = ({ data: { yOrientation } }: Props) => {
  return (
    <>
      {DEVICE_ORIENTATION.find((device) => device.id === yOrientation)?.display}
    </>
  );
};

export const daysBehind = ({ data }: Props): number | string => {
  if (!data.latest_ts) return '-';
  const diffInMinutes =
    calculateTimeStampFromCurrentTime(data.latest_ts) / (1000 * 60);
  const diffInHours = diffInMinutes / 60;

  if (diffInHours < 24) {
    const hours = Math.floor(diffInMinutes / 60);
    const remainingMinutes = Math.floor(diffInMinutes % 60);
    if (remainingMinutes === 0 && hours > 0) {
      return `${hours} hrs`;
    } else if (
      (remainingMinutes === 0 && hours === 0) ||
      (remainingMinutes > 0 && hours === 0)
    ) {
      return `${remainingMinutes} mins`;
    } else {
      return `${hours} hrs, ${remainingMinutes} mins`;
    }
  } else {
    // Calculate days and remaining hours
    const days = Math.floor(diffInHours / 24);
    const remainingHours = Math.floor(diffInHours % 24);
    if (remainingHours === 0) {
      return `${days} days`;
    } else {
      return `${days} days, ${remainingHours} hrs`;
    }
  }
};

export const ingestDate = ({ data }: Props): string => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  if (!data.latest_ts) return '-';

  return convertUtcToDisplayTime(
    Number(data.latest_ts),
    preferredTimeDisplayMode,
    timezone,
    'DD/MM/YYYY HH:mm'
  );
};

export const connectionDate = ({ data }: Props): string => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const lastDataReported = data?.latest_ts ? data.latest_ts : null;
  const lastConnected =
    data?.connection_status === 'connected'
      ? lastDataReported
      : data?.connection_updated_ts
      ? data.connection_updated_ts
      : null;
  if (!lastConnected || lastConnected === null) return '-';

  return convertUtcToDisplayTime(
    Number(lastConnected),
    preferredTimeDisplayMode,
    timezone,
    'DD/MM/YYYY HH:mm'
  );
};

export const BumperNumberForTable = ({
  data,
  latestTagData,
  assetsWithLiveData,
}: AssetListRendererProp) => {
  const asset = assetsWithLiveData.find(
    (item: any) => item.device.deviceId === (data as any).deviceId
  );
  const ignitionStatus = isAssetIgnition(latestTagData, asset);
  return (
    <>
      <Typography sx={{ color: 'white' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left',
            marginTop: '10px',
          }}
        >
          <StatusDot isActive={ignitionStatus} />
          {(data as any)?.assetBumperNumber}
        </Box>
      </Typography>
    </>
  );
};
export const lastLocation = ({ data }: Props): string => {
  return data?.last_location ? data.last_location : '-';
};

export const lastLocationTime = ({ data }: Props) => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  if (!data?.last_location_ts) return '-';

  return convertUtcToDisplayTime(
    data.last_location_ts,
    preferredTimeDisplayMode,
    timezone,
    'DD/MM/YYYY HH:mm'
  );
};
